import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Hero from '../components/Hero';
import TierCard from '../components/TierCard';
import QuantitySelector from '../components/QuantitySelector';
import MembershipCard from '../components/MembershipCard';
import Reveal from '../components/Reveal';
import HowItWorks from '../components/HowItWorks';
import Faq from '../components/Faq';
import AppDownloadBand from '../components/AppDownloadBand';
import TravelKitShowcase from '../components/TravelKitShowcase';
import Footer from '../components/Footer';
import StickyMobileCta from '../components/StickyMobileCta';
import { TIERS, getTier, calculateOrderTotal, formatUsd } from '../config/tiers';
import { MEMBERSHIP_TIERS, getMembershipTier, formatUsd as formatMembershipUsd } from '../config/membership';
import { parseToken } from '../utils/token';
import { trackEvent } from '../utils/tracking';
import './Landing.css';

export default function Landing() {
  const navigate = useNavigate();
  const plansRef = useRef(null);

  const token = useMemo(() => parseToken(window.location.search), []);
  const [planType, setPlanType] = useState('yearly');
  const [selectedMembershipKey, setSelectedMembershipKey] = useState('voyager');
  const [selectedTierKey, setSelectedTierKey] = useState('plus');
  const [quantity, setQuantity] = useState(token.travelerCount || 1);

  useEffect(() => {
    trackEvent('Page Viewed', {
      page: 'landing',
      personalized: token.valid,
      destination: token.destination,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePlanTypeChange = (nextPlanType) => {
    setPlanType(nextPlanType);
    trackEvent('Plan Type Toggled', { planType: nextPlanType });
  };

  const selectedMembershipTier = getMembershipTier(selectedMembershipKey);

  const handleMembershipContinue = () => {
    trackEvent('Membership CTA Clicked', { source: 'landing_yearly', tier: selectedMembershipKey });
    navigate(`/membership?tier=${selectedMembershipKey}`);
  };

  const handleSelectTier = (tierKey) => {
    setSelectedTierKey(tierKey);
    const tier = getTier(tierKey);
    trackEvent('Tier Selected', { tier: tierKey, price: tier.priceCents / 100 });
  };

  const handleQuantityChange = (nextQuantity) => {
    setQuantity(nextQuantity);
    trackEvent('Quantity Changed', { quantity: nextQuantity });
  };

  const selectedTier = getTier(selectedTierKey);
  const { multiTravelerDiscountCents, totalCents } = calculateOrderTotal(selectedTier, quantity);

  const handleContinue = () => {
    const revenue = totalCents / 100;
    trackEvent('Checkout Started', { tier: selectedTierKey, quantity, revenue });
    sessionStorage.setItem(
      'tp_selection',
      JSON.stringify({ tier: selectedTierKey, quantity, token })
    );
    navigate('/checkout');
  };

  return (
    <div className="tp-landing">
      <Header />
      <Hero
        token={token}
        onCtaClick={scrollToPlans}
        eyebrow="Travel+ Annual — exclusive to policyholders"
        headline="A whole year of trips, upgraded"
        subhead="Lounge access, eSIM data, and exclusive travel perks on every trip you take this year — one yearly membership, priced for how often you fly."
        ctaLabel="See annual plans"
      />

      <HowItWorks />

      <section className="tp-plans container" ref={plansRef} id="plans">
        <div className="tp-plan-toggle" role="tablist" aria-label="Plan type">
          <button
            type="button"
            role="tab"
            aria-selected={planType === 'yearly'}
            className={`tp-plan-toggle__option${planType === 'yearly' ? ' tp-plan-toggle__option--active' : ''}`}
            onClick={() => handlePlanTypeChange('yearly')}
          >
            Yearly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={planType === 'pertrip'}
            className={`tp-plan-toggle__option${planType === 'pertrip' ? ' tp-plan-toggle__option--active' : ''}`}
            onClick={() => handlePlanTypeChange('pertrip')}
          >
            Per trip
          </button>
        </div>

        <div key={planType} className="tp-plans__viewport">
          {planType === 'yearly' ? (
            <>
              <div className="tp-plans__grid">
                {MEMBERSHIP_TIERS.map((tier, i) => (
                  <Reveal key={tier.key} delay={i * 40}>
                    <MembershipCard
                      tier={tier}
                      selected={selectedMembershipKey === tier.key}
                      onSelect={setSelectedMembershipKey}
                    />
                  </Reveal>
                ))}
              </div>

              <div className="tp-plans__footer">
                <button
                  type="button"
                  className="btn btn-primary tp-plans__continue"
                  onClick={handleMembershipContinue}
                >
                  Continue with {selectedMembershipTier.name} — {formatMembershipUsd(selectedMembershipTier.priceCents)}/year
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="tp-tiers__grid">
                {TIERS.map((tier, i) => (
                  <Reveal key={tier.key} delay={i * 40}>
                    <TierCard
                      tier={tier}
                      selected={selectedTierKey === tier.key}
                      onSelect={handleSelectTier}
                    />
                  </Reveal>
                ))}
              </div>

              <div className="tp-tiers__footer">
                <QuantitySelector quantity={quantity} onChange={handleQuantityChange} />
                {multiTravelerDiscountCents > 0 && (
                  <p className="body-s tp-tiers__discount">
                    10% multi-traveler discount applied — you save {formatUsd(multiTravelerDiscountCents)}
                  </p>
                )}
                <button type="button" className="btn btn-primary tp-tiers__continue" onClick={handleContinue}>
                  Continue to checkout — {formatUsd(totalCents)}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <TravelKitShowcase />
      <AppDownloadBand />
      <Faq />
      <Footer />
      <StickyMobileCta onClick={scrollToPlans} />
    </div>
  );
}
